import type {
  BasketBooking,
  BasketClash,
  BasketTotals,
  BookingItem,
} from 'types/booking';
import type { PaymentMethod, PaymentMethodAllocations } from 'types/payment';
import { paymentMethodForBooking } from 'utils/bookings';

export interface State {
  items: BasketBooking[];
  totals: BasketTotals;
  hasPayAsYouGo: boolean;
  // Never actually set: FETCH_BASKET_SUCCESS reads `expiresAt` off its payload,
  // but the provider does not put it there — it writes the expiry to a cookie
  // instead, which is what the basket screen reads. See the follow-ups note.
  bookingsExpireAt?: Date;
  loading: boolean;
  submitting: boolean;
  error: boolean;
  clashes: BasketClash[];
  registeredPaymentSources: PaymentMethod[];
  servicesMissingPaymentMethods: string[];
  // Keyed by booking id, despite the previous `Array<any>` — the reducer builds
  // it with `acc[booking.id] = paymentMethod`.
  paymentMethodAllocations: PaymentMethodAllocations;
  confirmed: boolean;
  confirmedBookingItems: BookingItem[];
}

// PAY_AS_YOU_GO, PAY_IN_FULL, FETCH_PAYMENT_METHODS and BASKET_CONFIRMED have no
// case below; they fall through to the default and leave the state unchanged.
export type Action =
  | { type: 'FETCH_BASKET' }
  | {
      type: 'FETCH_BASKET_SUCCESS';
      payload: {
        items: BasketBooking[];
        totals: BasketTotals;
        hasPayAsYouGo: boolean;
        expiresAt?: Date;
      };
    }
  | { type: 'ADD_TO_BASKET' }
  | { type: 'ADDED_TO_BASKET' }
  | { type: 'ADD_TO_BASKET_ERROR' }
  | { type: 'SET_EXPIRED_ITEM_CLASHES'; payload: BasketClash }
  | { type: 'FETCH_PAYMENT_METHODS' }
  | {
      type: 'FETCH_PAYMENT_METHODS_SUCCESS';
      payload: { registeredPaymentSources: PaymentMethod[] };
    }
  | { type: 'CHECK_SERVICES_MISSING_PAYMENT_METHODS' }
  | { type: 'ALLOCATE_PAYMENT_METHODS_TO_BOOKINGS' }
  | { type: 'PAY_AS_YOU_GO' }
  | { type: 'PAY_IN_FULL' }
  | { type: 'SUBMIT_BOOKINGS' }
  | { type: 'SUBMIT_BOOKINGS_SUCCESS' }
  | { type: 'BASKET_CONFIRMED' };

export const initialState: State = {
  items: [],
  totals: {
    hasPayAsYouGo: true,
    payLater: {
      netCost: 10,
      grossCost: 12,
      vatAmount: 2,
      grossCostOnlinePayAsYouGo: 0,
      grossCostOfflinePayAsYouGo: 0,
    },
    payNow: {
      netCost: 10,
      grossCost: 12,
      vatAmount: 2,
      grossCostOnline: 0,
      grossCostOffline: 0,
    },
  },
  bookingsExpireAt: new Date(),
  hasPayAsYouGo: false,
  loading: false,
  submitting: false,
  error: false,
  clashes: [],
  registeredPaymentSources: [],
  servicesMissingPaymentMethods: [],
  paymentMethodAllocations: [],
  confirmed: false,
  confirmedBookingItems: [],
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'ADD_TO_BASKET': {
      return { ...state, submitting: true, error: false };
    }

    case 'ADDED_TO_BASKET': {
      return {
        ...state,
        submitting: false,
      };
    }

    case 'ADD_TO_BASKET_ERROR': {
      return {
        ...state,
        submitting: false,
        error: true,
      };
    }

    case 'FETCH_BASKET': {
      return { ...state, loading: true };
    }

    case 'FETCH_BASKET_SUCCESS': {
      return {
        ...state,
        loading: false,
        items: action.payload.items,
        totals: action.payload.totals,
        hasPayAsYouGo: action.payload.hasPayAsYouGo,
        bookingsExpireAt: action.payload.expiresAt,
        clashes: [],
      };
    }

    case 'SET_EXPIRED_ITEM_CLASHES': {
      return {
        ...state,
        loading: false,
        clashes: [...state.clashes, action.payload],
      };
    }

    case 'FETCH_PAYMENT_METHODS_SUCCESS': {
      return {
        ...state,
        registeredPaymentSources: action.payload.registeredPaymentSources,
      };
    }

    case 'CHECK_SERVICES_MISSING_PAYMENT_METHODS': {
      const overallCost = state.totals.payNow.grossCost;
      const servicesMissingPaymentMethods =
        overallCost === 0
          ? []
          : state.items
              .reduce<string[]>((acc, booking) => {
                const foundPaymentMethod = paymentMethodForBooking(
                  state.registeredPaymentSources,
                  booking
                );

                if (
                  !foundPaymentMethod &&
                  !acc.includes(booking.clientService.name) &&
                  !booking.offlinePayments
                ) {
                  acc.push(booking.clientService.name);
                }

                return acc;
              }, [])
              .sort((a: string, b: string) => a.localeCompare(b));

      return {
        ...state,
        servicesMissingPaymentMethods,
      };
    }

    case 'ALLOCATE_PAYMENT_METHODS_TO_BOOKINGS': {
      const overallCost = state.totals.payNow.grossCost;
      const paymentMethodAllocations =
        overallCost === 0
          ? {}
          : state.items.reduce<PaymentMethodAllocations>((acc, booking) => {
              const paymentMethod = paymentMethodForBooking(
                state.registeredPaymentSources,
                booking
              );

              if (paymentMethod) {
                acc[booking.id] = paymentMethod;
              }

              return acc;
            }, {});

      return {
        ...state,
        paymentMethodAllocations,
      };
    }

    case 'SUBMIT_BOOKINGS': {
      return {
        ...state,
        submitting: true,
      };
    }

    case 'SUBMIT_BOOKINGS_SUCCESS': {
      return {
        ...state,
        submitting: false,
        items: [],
        totals: initialState.totals,
      };
    }

    default:
      return state;
  }
};

export default reducer;
